import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAppStore } from '@/modules/shared/store/appStore';
import { getAccessToken } from '../../utils/helper';
import { STATIC_ROUTES } from '../../constants/routes';
import { hasSession, type SessionAffiliate } from './authGuard';

interface RouteWrapperProps {
  children: ReactNode;
}

/**
 * PrivateRoute - A wrapper component for protected routes
 * Redirects to login if user is not authenticated
 *
 * Two deliberate details, both previously wrong:
 *
 * 1. The affiliate comes from the **selector hook**, not `useAppStore.getState()`. `getState()` is a
 *    one-shot read that does not subscribe, so this component did not re-render when the affiliate
 *    landed — the `[affiliate]` effect dependency below could never fire on a store update, and
 *    whether a deep link survived depended on an unrelated parent re-render.
 *
 * 2. The bounce to `/` carries `state.from`. Without it the destination was discarded and
 *    `PublicRoute` sent the user to its hardcoded `/create-leads` — which is why a deep link opened
 *    in a new tab showed the login screen and then landed on the wrong page.
 *
 * The `isLoading` first-render deferral is kept on purpose: it gives an in-flight affiliate one tick
 * to arrive before this decides to redirect. Removing it makes a late affiliate strictly worse.
 */
const PrivateRoute = ({ children }: RouteWrapperProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const affiliate = useAppStore((state) => state.affiliate) as SessionAffiliate | null;
  const location = useLocation();

  useEffect(() => {
    // Simple auth check - just check if we have a token in localStorage
    const accessToken = getAccessToken();

    // If we have a token and the affiliate id, we're authenticated. Shared with `PublicRoute` via
    // `hasSession` so the two can never disagree and bounce the user between them.
    setIsAuthenticated(hasSession(accessToken, affiliate));
    setIsLoading(false);
  }, [affiliate]);

  if (isLoading) {
    return <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto my-4"></div>;
  }

  const path = '/';
  const isStaticRoute = (STATIC_ROUTES as Record<string, boolean>)[window.location.pathname];

  // Static routes are not a destination worth returning to, so they carry no `from`.
  const redirect = <Navigate to={path} replace state={isStaticRoute ? undefined : { from: location }} />;

  if (!affiliate || isStaticRoute) {
    return redirect;
  }

  return isAuthenticated ? <>{children}</> : redirect;
};

export default PrivateRoute;
